import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CatalogTundish } from "../../../models/CatalogTundish";
import { useTableData } from "../hooks/useTableData";
import { useTundishOptionsFunctions } from "../hooks/useTundishOptionsFunctions";
import { TundishModal } from "./TundishModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogTundishesFromAPI: Function;
  isLoading: boolean;
  tundishId: string | undefined;
};

export const TableTundish = ({
  getCatalogTundishesFromAPI,
  isLoading,
  tundishId,
}: Props) => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [tundish, setTundish] = useState<CatalogTundish | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewTundish = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setTundish(null);
    navigate(`/configuration/caster-tundishes/new`);
  };

  const { getMenuOptions } = useTundishOptionsFunctions({
    setTundish,
    setShowModal,
    handleNewTundish,
  });
  const { rows, columnDefs } = useTableData({
    setTundish,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setTundish(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setTundish(event.data);
      setShowModal("edit");
      navigate(
        `/configuration/caster-tundishes/${
          (event.data as CatalogTundish).TundishId
        }`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/caster-tundishes/${
          (event.data as CatalogTundish).TundishId
        }`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "tundishContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setTundish(null);
    navigate(`/configuration/caster-tundishes/`);
  };

  useEffect(() => {
    if (tundishId) {
      if (tundishId === "new") {
        handleNewTundish();
      } else {
        let tundishNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogTundish).TundishId === Number(tundishId)) {
            tundishNode = n;
            return true;
          }
        });

        if (tundishNode!) {
          const pageSize = gridApi?.paginationGetPageSize();
          const pageNumber = Math.floor(
            (tundishNode!.rowIndex ?? 0) / pageSize!,
          );
          gridApi?.paginationGoToPage(pageNumber);

          tundishNode.setSelected(true);
          setTundish(tundishNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "tundishContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "1px 0 10px", fontWeight: 600 }}
          >
            Tundishes
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogTundish>
              rowData={rows || []}
              columnDefs={columnDefs as any}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              onGridReady={(params) => setGridApi(params.api)}
              loading={isLoading}
              getRowId={(params) => String(params.data.TundishId)}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="TUNDISH" onClick={handleNewTundish} />
        </Grid>
      </Grid>
      <TundishModal
        tundish={tundish}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogTundishesFromAPI}
      />
    </>
  );
};
